/**
 * The signed-in user's application-owned details — title, bio, phone, location, initials
 * override and avatar color — over the users tRPC router.
 *
 * Not persisted. Loading and resetting follow the signed-in user id; the watchers in
 * session_scope.ts drive them, not components.
 */
import type {IAvatarMember} from '@c/common/avatar_types';
import type {IUserDetails} from '@models/user_details';
import {trpcClient} from '@shared_ui/trpc_client';
import {buildTrpcErrorMessage} from '@shared_ui/trpc_errors';
import {useAuthStore} from '@src/store/auth_store';
import {getNameInitials} from '@u/string';
import {defineStore} from 'pinia';

export interface IUserState {
   /** The signed-in user's details; null until loaded. */
   details: IUserDetails | null;
   isLoaded: boolean;

   /** Which user the loaded details belong to (guards account switches). */
   loadedForUserId: string | null;
}

/** Details fields a save may change; an absent key leaves the stored value alone. */
export type UserDetailsPatch = Partial<
   Pick<
      IUserDetails,
      | 'title'
      | 'bio'
      | 'phone'
      | 'location'
      | 'initials'
      | 'avatarColor'
      | 'themePreference'
      | 'densityPreference'
   >
>;

export const useUserStore = defineStore('user', {
   state: (): IUserState => ({
      details: null,
      isLoaded: false,
      loadedForUserId: null,
   }),

   getters: {
      /** Avatar for the signed-in user; null until the name and details are both known. */
      myAvatar(state): IAvatarMember | null {
         const name = useAuthStore().user?.name;
         if (name == null || state.details == null) {
            return null;
         }

         return {
            name,
            color: state.details.avatarColor,
            initials: state.details.initials ?? getNameInitials(name),
         };
      },
   },

   actions: {
      /**
       * Load the details for the signed-in user unless they are already loaded for that
       * user. Resets the store when signed out. Cheap to call repeatedly.
       */
      async ensureLoaded(): Promise<void> {
         const user_id = useAuthStore().user?.id ?? null;
         if (user_id == null) {
            this.$reset();
            return;
         }
         if (this.isLoaded && this.loadedForUserId === user_id) {
            return;
         }

         this.$reset();
         this.loadedForUserId = user_id;
         await this.load();
      },

      /** Read the signed-in user's details, creating the row on the server if it is missing.
       * A failure leaves isLoaded false so the next trigger retries. */
      async load(): Promise<void> {
         try {
            this.details = await trpcClient().users.getMyUser.query();
            this.isLoaded = true;
         } catch {
            this.isLoaded = false;
         }
      },

      /** Write the given fields to the signed-in user's details. Returns an error message or null. */
      async save(patch: UserDetailsPatch): Promise<string | null> {
         try {
            this.details = await trpcClient().users.updateMyUser.mutate(patch);
            this.isLoaded = true;
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not save your profile details');
         }
      },
   },
});
